import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';

/**
 * Descarta la sesión guardada y arranca un login limpio.
 *
 * <p>`clearStaleState` limpia el state guardado y `removeUser` el usuario. Sin eso, una sesión
 * inservible sobrevive a los reintentos y el único remedio es borrar los datos del sitio a mano
 * (#136).
 *
 * <p>El store por default de `oidc-client-ts` es `sessionStorage`, no `localStorage`: el JSDoc
 * original decía otra cosa y se corrige acá, que es donde va a leerlo el que venga.
 *
 * <p>Vive en su propio módulo porque lo usan dos lugares: la pantalla de error de `AutoLogin`
 * —para el que nunca llegó a entrar— y el aviso de sesión vencida —para el que estaba trabajando
 * (#168). Que los dos llamen a lo mismo es lo que evita que se separen con el tiempo.
 *
 * <p>El optional chaining es deliberado: hay un test que fija que no rompa si la versión de la
 * librería no expone la limpieza.
 */
export const useRestartLogin = () => {
  const auth = useAuth();

  return useCallback(async () => {
    try {
      await auth.clearStaleState?.();
      await auth.removeUser?.();
    } catch (err) {
      // Si la limpieza falla igual conviene intentar el login.
      console.warn('AutoLogin - No se pudo limpiar la sesión guardada:', err);
    }
    // react-oidc-context@3.3.0 NO propaga el rechazo de los navigator: atrapa, despacha ERROR y
    // devuelve `null` (react-oidc-context.js, envoltura de navigatorKeys). En el camino feliz
    // navega y resuelve `undefined`, así que `null` es la única señal de que falló — y sin
    // convertirla en excepción, ningún llamador se entera: el botón del aviso quedaría
    // deshabilitado para siempre y AutoLogin en "Verificando sesión…" indefinido. Es el callejón
    // sin salida de #136 volviendo por otra puerta.
    const resultado = await auth.signinRedirect();
    if (resultado === null) {
      throw new Error('El servidor de autenticación rechazó el pedido de login.');
    }
    return resultado;
  }, [auth]);
};
